// File validation utilities for secure file uploads

// Allowed MIME types by category
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

// Size limits in bytes
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB (images are optimized before upload)
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB

// Extension to MIME type mapping for validation
const EXTENSION_TO_MIME: Record<string, string[]> = {
  'jpg': ['image/jpeg'],
  'jpeg': ['image/jpeg'],
  'png': ['image/png'],
  'gif': ['image/gif'],
  'webp': ['image/webp'],
  'pdf': ['application/pdf'],
  'doc': ['application/msword'],
  'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  'ppt': ['application/vnd.ms-powerpoint'],
  'pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
};

// Safe extensions we allow
const SAFE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const SAFE_DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx'];

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  sanitizedExtension?: string;
}

/**
 * Validates a file for image uploads
 * Checks: file type, file size, extension matches MIME type
 */
export function validateImageFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > MAX_IMAGE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds limit (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB)`,
    };
  }

  // Check MIME type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: JPEG, PNG, GIF, WebP`,
    };
  }

  // Extract and validate extension
  const extension = getFileExtension(file.name);
  if (!extension || !SAFE_IMAGE_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file extension. Allowed: ${SAFE_IMAGE_EXTENSIONS.join(', ')}`,
    };
  }

  // Verify extension matches MIME type
  const allowedMimes = EXTENSION_TO_MIME[extension];
  if (!allowedMimes || !allowedMimes.includes(file.type)) {
    return {
      valid: false,
      error: `File extension doesn't match file type`,
    };
  }

  return {
    valid: true,
    sanitizedExtension: extension,
  };
}

/**
 * Validates a file for document uploads (including images)
 * Checks: file type, file size, extension matches MIME type
 */
export function validateDocumentFile(file: File): FileValidationResult {
  // Check if it's an image
  if (ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
    // For images in document context, use image size limit
    if (file.size > MAX_IMAGE_SIZE) {
      return {
        valid: false,
        error: `Image size exceeds limit (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB)`,
      };
    }
    
    const extension = getFileExtension(file.name);
    if (!extension || !SAFE_IMAGE_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        error: `Invalid image extension`,
      };
    }

    const allowedMimes = EXTENSION_TO_MIME[extension];
    if (!allowedMimes || !allowedMimes.includes(file.type)) {
      return {
        valid: false,
        error: `File extension doesn't match file type`,
      };
    }

    return {
      valid: true,
      sanitizedExtension: extension,
    };
  }

  // Check document size
  if (file.size > MAX_DOCUMENT_SIZE) {
    return {
      valid: false,
      error: `File size exceeds limit (max ${MAX_DOCUMENT_SIZE / 1024 / 1024}MB)`,
    };
  }

  // Check MIME type
  if (!ALLOWED_DOCUMENT_TYPES.includes(file.type as typeof ALLOWED_DOCUMENT_TYPES[number])) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: PDF, DOC, DOCX, PPT, PPTX, or images`,
    };
  }

  // Extract and validate extension
  const extension = getFileExtension(file.name);
  if (!extension || !SAFE_DOCUMENT_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file extension`,
    };
  }

  // Verify extension matches MIME type
  const allowedMimes = EXTENSION_TO_MIME[extension];
  if (!allowedMimes || !allowedMimes.includes(file.type)) {
    return {
      valid: false,
      error: `File extension doesn't match file type`,
    };
  }

  return {
    valid: true,
    sanitizedExtension: extension,
  };
}

/**
 * Safely extract file extension (lowercase, sanitized)
 */
function getFileExtension(filename: string): string | null {
  const parts = filename.split('.');
  if (parts.length < 2) return null;
  
  const extension = parts[parts.length - 1].toLowerCase().trim();
  
  // Sanitize: only allow alphanumeric
  if (!/^[a-z0-9]+$/.test(extension)) return null;
  
  // Limit length
  if (extension.length > 10) return null;
  
  return extension;
}

/**
 * Generate a safe filename with server-generated components
 * Uses the validated extension, not the user-provided one
 */
export function generateSafeFilename(folder: string, validatedExtension: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${folder}/${timestamp}-${random}.${validatedExtension}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FILE_LIMITS = {
  maxImageSize: MAX_IMAGE_SIZE,
  maxDocumentSize: MAX_DOCUMENT_SIZE,
  allowedImageExtensions: SAFE_IMAGE_EXTENSIONS,
  allowedDocumentExtensions: [...SAFE_IMAGE_EXTENSIONS, ...SAFE_DOCUMENT_EXTENSIONS],
} as const;
